import artSpec from "../art/art-spec.json";
import type { ArtAsset } from "../art/validate";
import type { PreviousPetView } from "../ui/view-model";
import { SCENE_HEIGHT, SCENE_WIDTH, drawFrame, drawTankScene, moodToFrame, type Canvas2D } from "./draw";

/**
 * The Monday hatch sequence (docs/SPEC.md section 2), copied from `renderAnim` in docs/handoff/006-shell-reference.html:
 * last week's pet goes into the book (or rests at the sanctuary), a new egg arrives, wobbles, cracks and hatches.
 */
const T = { over: 1400, leaving: 3600, incoming: 4200, wobble: 7000, hatch: 7800 } as const;
export const HATCH_END = T.hatch;

export type HatchPhase = "over" | "leaving" | "incoming" | "wobble" | "hatch" | "done";

export interface HatchFrame {
  phase: HatchPhase;
  /** Progress inside the phase, 0 to 1. */
  u: number;
  caption: string;
  done: boolean;
}

export function stillCaption(previous: PreviousPetView): string {
  return previous.outcome === "lost"
    ? `${previous.name} rests at the sanctuary. Lost pets are not added to the book.`
    : `${previous.name} is in your picture book with ${previous.points} points.`;
}

/** What the sequence shows `elapsedMs` after it starts. Without a previous pet it starts at the egg. */
export function hatchFrameAt(elapsedMs: number, previous: PreviousPetView | null): HatchFrame {
  const e = Math.max(0, elapsedMs) + (previous ? 0 : T.incoming);
  const within = (from: number, to: number) => Math.min(1, Math.max(0, (e - from) / (to - from)));
  const name = previous?.name ?? "";
  const lost = previous?.outcome === "lost";
  if (e < T.over) {
    return {
      phase: "over",
      u: within(0, T.over),
      caption: `${name}'s week is over: ${previous?.points ?? 0} points${lost ? " (lost)" : ""}.`,
      done: false,
    };
  }
  if (e < T.leaving) {
    return {
      phase: "leaving",
      u: within(T.over, T.leaving),
      caption: lost ? `${name} rests at the sanctuary. Lost pets are not added to the book.` : `${name} goes into your picture book.`,
      done: false,
    };
  }
  if (e < T.incoming) return { phase: "incoming", u: within(T.leaving, T.incoming), caption: "A new egg is on its way…", done: false };
  if (e < T.wobble) return { phase: "wobble", u: within(T.incoming, T.wobble), caption: "The egg is wobbling…", done: false };
  if (e < T.hatch) return { phase: "hatch", u: within(T.wobble, T.hatch), caption: "It hatched!", done: false };
  return { phase: "done", u: 1, caption: "It hatched!", done: true };
}

function pal(ramp: keyof typeof artSpec.masterPalette, index: number): string {
  const colour = artSpec.masterPalette[ramp][index];
  if (!colour) throw new Error(`Missing master palette colour ${ramp}[${index}]`);
  return colour;
}

const INK = pal("ink", 0);
const COVER = pal("umber", 2);
const PAGE = pal("neutral", 4);
const PAGE_EDGE = pal("cream", 1);
const SPINE = pal("cream", 0);
const EGG_SHELL = pal("neutral", 4);
const EGG_SHADE = pal("neutral", 3);
const SPARKLE = pal("gold", 4);
const SANCTUARY_DARK = pal("blue", 0);
const HEART_LINE = pal("pink", 0);
const HEART = pal("pink", 1);
const FLASH = pal("neutral", 4);
const BOOK_HEART = pal("red", 2);

const HEART_ROWS = [".oo.oo.", "oRrorro", "orrrrro", ".orrro.", "..oro..", "...o..."] as const;

function rect(ctx: Canvas2D, x: number, y: number, w: number, h: number, colour: string): void {
  ctx.fillStyle = colour;
  ctx.fillRect(x, y, w, h);
}

function outlined(ctx: Canvas2D, x: number, y: number, w: number, h: number, colour: string): void {
  rect(ctx, x, y, w, h, INK);
  rect(ctx, x + 1, y + 1, w - 2, h - 2, colour);
}

function withAlpha(ctx: Canvas2D, alpha: number, draw: () => void): void {
  ctx.globalAlpha = Math.max(0, Math.min(1, alpha));
  draw();
  ctx.globalAlpha = 1;
}

function drawBook(ctx: Canvas2D, offset: number): void {
  const y = 40 + Math.round(offset);
  outlined(ctx, 20, y, 40, 18, COVER);
  outlined(ctx, 22, y + 2, 17, 14, PAGE);
  outlined(ctx, 41, y + 2, 17, 14, PAGE);
  rect(ctx, 39, y + 2, 2, 14, SPINE);
  for (const j of [6, 8, 10]) rect(ctx, 24, y + j, 13, 1, PAGE_EDGE);
  outlined(ctx, 44, y + 4, 11, 8, PAGE);
  [".r.r.", "rrrrr", ".rrr.", "..r.."].forEach((row, ry) => {
    for (let rx = 0; rx < row.length; rx++) if (row[rx] === "r") rect(ctx, 47 + rx, y + 6 + ry, 1, 1, BOOK_HEART);
  });
  rect(ctx, 43, y + 13, 13, 1, PAGE_EDGE);
}

function drawHeart(ctx: Canvas2D, x: number, y: number): void {
  HEART_ROWS.forEach((row, ry) => {
    for (let rx = 0; rx < row.length; rx++) {
      const ch = row[rx];
      if (ch === "o") rect(ctx, x + rx, y + ry, 1, 1, HEART_LINE);
      else if (ch === "r") rect(ctx, x + rx, y + ry, 1, 1, HEART);
      else if (ch === "R") rect(ctx, x + rx, y + ry, 1, 1, FLASH);
    }
  });
}

const CRACKS = [
  [4, 3],
  [6, 5],
  [4, 7],
  [7, 9],
  [5, 11],
  [8, 13],
] as const;

function drawEgg(ctx: Canvas2D, offsetX: number, cracks: number): void {
  rect(ctx, 31, 57, 18, 1, SANCTUARY_DARK);
  const left = 33 + offsetX;
  const top = 41;
  const rows = 17;
  for (let r = 0; r < rows; r++) {
    const t = (r + 0.5) / rows;
    const half = Math.round(7.5 * Math.pow(Math.sin(Math.PI * t), 0.7) * (t < 0.5 ? 0.88 : 1));
    const cx = left + 7;
    rect(ctx, cx - half, top + r, half * 2 + 1, 1, INK);
    if (half > 1) rect(ctx, cx - half + 1, top + r, half * 2 - 1, 1, t > 0.62 ? EGG_SHADE : EGG_SHELL);
  }
  for (let i = 0; i < Math.min(cracks, CRACKS.length - 1); i++) {
    const a = CRACKS[i]!;
    const b = CRACKS[i + 1]!;
    rect(ctx, left + a[0], top + a[1], 1, 1, INK);
    rect(ctx, left + b[0], top + b[1], 1, 1, INK);
  }
}

/** `dy` moves the pet down from its resting place: a swimmer floats at y 28, a land pet stands on the ground line at y 48. */
function drawPet(ctx: Canvas2D, asset: ArtAsset, tick: number, dy = 0): void {
  const name = moodToFrame(asset.class, "happy", tick);
  const frame = name in asset.frames ? name : (Object.keys(asset.frames)[0] ?? name);
  const restY = asset.class === "swimmer" ? PET_Y : 48;
  drawFrame(ctx, asset, frame, Math.round(SCENE_WIDTH / 2) - asset.anchor.x, restY + dy - asset.anchor.y);
}

function sparkles(ctx: Canvas2D, elapsed: number): void {
  const spots = [
    [24, 18],
    [56, 22],
    [48, 12],
    [28, 34],
  ] as const;
  spots.forEach(([x, y], i) => {
    if ((Math.floor(elapsed / 180) + i) % 2 === 0) return;
    for (const [dx, dy] of [[0, 0], [-1, 0], [1, 0], [0, -1], [0, 1]] as const) rect(ctx, x + dx, y + dy, 1, 1, SPARKLE);
  });
}

const PET_Y = 28;

/**
 * Paints one frame of the sequence on the 80x60 grid. `frame` null draws the still end state (reduced motion): the old
 * pet already in the book or resting.
 */
export function drawHatchScene(
  ctx: Canvas2D,
  asset: ArtAsset,
  previous: PreviousPetView | null,
  frame: HatchFrame | null,
  tick: number,
  reducedMotion: boolean,
  /** The animal that hatches (this week's); `asset` is last week's pet. */
  newAsset: ArtAsset = asset,
): void {
  drawTankScene(ctx, SCENE_WIDTH, SCENE_HEIGHT, { tick, reducedMotion });
  const lost = previous?.outcome === "lost";
  const ease = (u: number) => u * u * (3 - 2 * u);

  if (!frame) {
    if (!previous) return;
    if (lost) withAlpha(ctx, 0.4, () => drawPet(ctx, asset, 0));
    else drawBook(ctx, 0);
    return;
  }

  const { phase, u } = frame;
  if (phase === "over") {
    drawPet(ctx, asset, tick);
    if (lost) withAlpha(ctx, 0.5, () => rect(ctx, 0, 0, SCENE_WIDTH, SCENE_HEIGHT, SANCTUARY_DARK));
    sparkles(ctx, u * T.over);
  } else if (phase === "leaving") {
    const ez = ease(u);
    if (lost) {
      withAlpha(ctx, 1 - ez, () => drawPet(ctx, asset, tick));
      for (let h = 0; h < 3; h++) {
        const uh = (u + h * 0.2) % 1;
        withAlpha(ctx, 1 - uh, () => drawHeart(ctx, 34 + h * 7, Math.round(40 - uh * 26)));
      }
    } else {
      withAlpha(ctx, 1 - ez * 0.85, () => drawPet(ctx, asset, tick, Math.round(ez * 14)));
      drawBook(ctx, Math.max(0, (1 - Math.min(1, u * 2.2)) * 24));
    }
  } else if (phase === "incoming") {
    if (!lost && previous) drawBook(ctx, u * 24);
  } else if (phase === "wobble") {
    const e = u * (T.wobble - T.incoming);
    drawEgg(ctx, reducedMotion ? 0 : Math.round(Math.sin(e / 90) * (1 + u * 2)), Math.floor(u * 6));
  } else {
    withAlpha(ctx, u, () => drawPet(ctx, newAsset, tick));
    withAlpha(ctx, 1 - u, () => rect(ctx, 0, 0, SCENE_WIDTH, SCENE_HEIGHT, FLASH));
  }
}
